import { inject, Injectable } from '@angular/core'
import { LocalChannel } from '../interfaces/local-channel'
import { IHTVPlatform } from './platform'
import { ChannelPosition } from '../pages/channel/channel-position'
import { MonitoringMemoryData, MonitoringStorageData, SystemData } from '../interfaces/monitoring'
import { DVBTChannel, UDPChannel, WebChannel } from '../interfaces/channel'
import { TizenInternal } from './tizen-internal'
import { TizenPalette } from './tizen-palette'
import { LocalAppLaunchMode, TVApp } from '../interfaces/local-app'
import { ChannelTrackData, ChannelTrackDataFlags, CurrentChannelData } from '../interfaces/channel-track-data'
import { PowerState } from '../interfaces/power-state'
import { BehaviorSubject } from 'rxjs'
import { environment } from '../environments/environment'
import {
  TizenAudioInfo,
  TizenAudioType,
  TizenNetworkState,
  TizenSubtitleInfo,
  TizenSubtitleMode,
  TizenSystemInfoCpu,
  TizenSystemInfoStorage,
} from './tizen-types'
import { LogService, LocalMediaFile, Media } from 'htv-lib'
import { NetworkConnectionType, NetworkData, NetworkSoftAP, NetworkState, NetworkWifiData } from '../interfaces/network'
import { StatusSuccessDto } from '../interfaces/dto/status-response-dto'
import { RouterService, RouterPage } from '../services/router.service'
import { GoogleCastState } from '../interfaces/local-googlecast'
import { ExternalInputData } from '../services/external-input.service'
import { VODStatus, VODVideo } from '../interfaces/vod'

declare const tizen: any | undefined
declare const webapis: any | undefined

interface ApplicationInformation {
  id: string
  name: string
  iconPath: string
  version: string
  show: boolean
  categories: string[]
  installDate: Date
  size: number
  packageId: string
}

@Injectable({
  providedIn: 'root',
})
export class TizenPlatform implements IHTVPlatform {
  private tizenInternal: TizenInternal
  private tizenPalette = inject(TizenPalette)

  private readonly MEDIA_PATH = 'wgt-private/medias'
  private readonly NETFLIX_APP_ID = '3201907018807'
  private readonly NETFLIX_APP_NAME = 'org.tizen.netflix-app'
  private readonly NETFLIX_TOKEN = 'KlNiKgmDpYZH557wGXLOwDdiwxTHzmhnA4tVy0dIUBe9S5q6T39md83vza0OSXPP'
  private readonly GOOGLECAST_TOKEN = 'KlNiKgmDpYZH557wGXLOwDdiwxTHzmhnA4tVy0dIUBe9S5q6T39md83vza0OSXPP'

  isSubtitleEnabled = false

  powerState?: PowerState
  powerStateTimer: any
  readonly powerStateTimerDelayInMs = 1000

  gcInitializedSubject?: BehaviorSubject<boolean | undefined>
  applicationInstalledSubject?: BehaviorSubject<string[]>
  channelChangedSubject?: BehaviorSubject<boolean>

  netflixEnabled = false
  netflixToken?: string
  netflixAppId?: string
  netflixAppName?: string
  isNetflixInstalled = false

  private isSeeking = false

  constructor(
    private logService: LogService,
    private routerService: RouterService
  ) {
    this.logService.debug('TizenPlatform loaded')
    this.tizenInternal = new TizenInternal(logService)
  }

  id(): string {
    return 'TizenPlatform'
  }

  async init(): Promise<void> {
    await this.loadScript('$WEBAPIS/webapis/webapis.js')
    await this.loadScript('$WEBAPIS/avplayextension/avplayextension.js')

    this.registerRemoteKeys()
    this.initStorage()
    this.registerVolumeChangeListener()
    this.initBroadcastDataService()
  }

  afterRegistrationHook(deviceId: string | undefined): void {
    this.setupCustomAppInfo(deviceId)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async afterStatusHook(status: StatusSuccessDto): Promise<void> {
    if (status.htvConfig) {
      const htvConfig = status.htvConfig

      if (htvConfig && htvConfig.netflix) {
        this.netflixEnabled = htvConfig?.netflix?.enabled ?? false
        this.netflixToken = htvConfig?.netflix?.token ?? this.NETFLIX_TOKEN
        this.netflixAppId = htvConfig?.netflix?.appId ?? this.NETFLIX_APP_ID
        this.netflixAppName = htvConfig?.netflix?.appName ?? this.NETFLIX_APP_NAME
      }

      if (htvConfig.tizenConfig?.paletteConfig) {
        await this.tizenPalette.configure(htvConfig.tizenConfig.paletteConfig, this.MEDIA_PATH)
      }
    }

    const tvName = `[TV] ${status.deviceName}`
    this.tizenInternal.checkAndUpdateTVName(tvName)

    this.handleNetflixApp()
    return Promise.resolve()
  }

  enableGuestMode(): Promise<void> {
    try {
      webapis.systemcontrol.deleteAppPrivateInfo('OFF')
      this.logService.debug(`[Tizen] deleteAppPrivateInfo('OFF') success`)
      return Promise.resolve()
    } catch (err: any) {
      this.logService.error(`Could not set deleteAppPrivateInfo('OFF'), error: (${err?.code}) ${err?.message}`)
      return Promise.reject()
    }
  }

  disableGuestMode(): Promise<void> {
    try {
      webapis.systemcontrol.deleteAppPrivateInfo('ON')
      this.logService.debug(`[Tizen] deleteAppPrivateInfo('ON') success`)
      return Promise.resolve()
    } catch (err: any) {
      this.logService.error(`Could not set deleteAppPrivateInfo('ON'), error: (${err?.code}) ${err?.message}`)
      return Promise.reject()
    }
  }

  clearData(): Promise<void> {
    return Promise.resolve()
  }

  getSystemData(): Promise<SystemData> {
    return Promise.resolve({
      model: this.tizenInternal.getModel(),
      manufacturer: this.tizenInternal.getLicensedVendor(),
      serialNumber: this.tizenInternal.getSerialNumber(),
      firmwareVersion: this.tizenInternal.getFirmware(),
      appVersion: this.tizenInternal.getAppVersion(),
    })
  }

  getPlatformData(): Promise<object> {
    const data: { [key: string]: any } = {}

    data['userAgent'] = navigator.userAgent
    data['appInfo'] = this.tizenInternal.getCurrentAppInfo()
    data['appVersion'] = this.tizenInternal.getAppVersion()

    const productInfoData: any = {}
    productInfoData['version'] = this.tizenInternal.getProductInfoVersion()
    productInfoData['firmware'] = this.tizenInternal.getFirmware()
    productInfoData['duid'] = this.tizenInternal.getDuid()
    productInfoData['modelCode'] = this.tizenInternal.getModelCode()
    productInfoData['model'] = this.tizenInternal.getModel()
    productInfoData['smartTVServerType'] = this.tizenInternal.getSmartTVServerType()
    productInfoData['smartTVServerVersion'] = this.tizenInternal.getSmartTVServerVersion()
    productInfoData['isUdPanelSupported'] = this.tizenInternal.isUdPanelSupported()
    productInfoData['is8KPanelSupported'] = this.tizenInternal.is8KPanelSupported()
    productInfoData['realModel'] = this.tizenInternal.getRealModel()
    productInfoData['isWallModel'] = this.tizenInternal.isWallModel()
    productInfoData['getLocalSet'] = this.tizenInternal.getLocalSet()
    productInfoData['isUHDAModel'] = this.tizenInternal.isUHDAModel()
    productInfoData['isDisplayRotatorSupported'] = this.tizenInternal.isDisplayRotatorSupported()
    productInfoData['licensedVendor'] = this.tizenInternal.getLicensedVendor()
    productInfoData['configKeyDataService'] = this.tizenInternal.getSystemConfig(
      webapis.productinfo.ProductInfoConfigKey.CONFIG_KEY_DATA_SERVICE
    )
    productInfoData['configKeyServiceCountry'] = this.tizenInternal.getSystemConfig(
      webapis.productinfo.ProductInfoConfigKey.CONFIG_KEY_SERVICE_COUNTRY
    )
    data['productinfo'] = productInfoData

    const systemcontrolData: any = {}
    systemcontrolData['version'] = this.tizenInternal.getSystemControlVersion()
    systemcontrolData['serialNumber'] = this.tizenInternal.getSerialNumber()
    systemcontrolData['customAppInfo'] = this.tizenInternal.getCustomAppInfo()
    data['systemcontrol'] = systemcontrolData

    const remotepowerData: any = {}
    remotepowerData['version'] = this.tizenInternal.getRemotePowerVersion()
    remotepowerData['powerState'] = this.tizenInternal.getPowerState()
    remotepowerData['virtualStandbyMode'] = this.tizenInternal.getVirtualStandbyMode()
    data['remotepower'] = remotepowerData

    const wrapPromise = (key: string, promise: Promise<any>) => {
      return promise.then((data: any) => ({ key: key, data: data }))
    }

    const promises = [
      wrapPromise('SystemInfoDisplay', this.tizenInternal.getSystemInfoPropertyValue('DISPLAY')),
      wrapPromise('SystemInfoPanel', this.tizenInternal.getSystemInfoPropertyValue('PANEL')),
      wrapPromise('SystemInfoDeviceOrientation', this.tizenInternal.getSystemInfoPropertyValue('DEVICE_ORIENTATION')),
      wrapPromise('SystemInfoBuild', this.tizenInternal.getSystemInfoPropertyValue('BUILD')),
      wrapPromise('SystemInfoLocale', this.tizenInternal.getSystemInfoPropertyValue('LOCALE')),
      wrapPromise('SystemInfoPeripheral', this.tizenInternal.getSystemInfoPropertyValue('PERIPHERAL')),
      wrapPromise('SystemInfoVideosource', this.tizenInternal.getSystemInfoPropertyValue('VIDEOSOURCE')),
      wrapPromise('SystemInfoSourceInfo', this.tizenInternal.getSystemInfoPropertyValue('SOURCE_INFO')),
      wrapPromise('SystemInfoServiceCountry', this.tizenInternal.getSystemInfoPropertyValue('SERVICE_COUNTRY')),
    ]

    return Promise.allSettled(promises)
      .then(results => {
        for (let i = 0; i < results.length; i++) {
          const result = results[i]
          if (result.status === 'fulfilled') {
            data[result.value.key] = result.value.data
          }
        }
        return data
      })
      .catch(() => {
        return data
      })
  }

  getCpuUsage(): Promise<number> {
    return this.tizenInternal.getSystemInfoPropertyValue('CPU').then((data: TizenSystemInfoCpu) => {
      return data.load
    })
  }

  getMemoryData(): Promise<MonitoringMemoryData> {
    return Promise.resolve({
      availableMemory: this.tizenInternal.getAvailableMemory(),
      totalMemory: this.tizenInternal.getTotalMemory(),
    })
  }

  getStorageData(): Promise<MonitoringStorageData[]> {
    return this.tizenInternal.getSystemInfoPropertyValue('STORAGE').then((data: TizenSystemInfoStorage) => {
      return data.units.map(unit => ({
        type: unit.type,
        availableCapacity: unit.availableCapacity,
        capacity: unit.capacity,
        isRemovable: unit.isRemovable,
      }))
    })
  }

  getTemperature(): Promise<number> {
    return new Promise((resolve, reject) => {
      if (this.tizenInternal.hasCapability('http://tizen.org/feature/sensor.temperature')) {
        try {
          resolve(webapis.tvinfo.getTvInfoValue('BOARD_TEMPERATURE'))
        } catch (err: any) {
          this.logService.error(`Could not retrieve temperature, error = ${err?.message}`)
          reject()
        }
      } else {
        reject()
      }
    })
  }

  getValue(key: string): any {
    return localStorage.getItem(key)
  }

  setValue(key: string, value: any): void {
    localStorage.setItem(key, value)
  }

  removeValue(key: string): void {
    localStorage.removeItem(key)
  }

  clearValues(): void {
    localStorage.clear()
  }

  loadScript(path: string): Promise<boolean> {
    return new Promise(resolve => {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.onload = () => {
        this.logService.debug('Loaded script ' + path)
        resolve(true)
      }
      script.onerror = (err: any) => {
        this.logService.error('Could not load script ' + path)
        this.logService.error(err)
        resolve(false)
      }
      script.src = path
      document.head.appendChild(script)
    })
  }

  registerRemoteKeys(): void {
    const supportedKeys = tizen.tvinputdevice.getSupportedKeys()
    for (const supportedKey of supportedKeys) {
      try {
        this.logService.debug(`Registering Key ${supportedKey.code} ${supportedKey.name}`)
        tizen.tvinputdevice.registerKey(supportedKey.name)
      } catch (err: any) {
        this.logService.error('Could not register key ' + supportedKey.name)
        this.logService.error(err.message)
      }
    }
    const specialKeysObj = [
      ['Netflix'],
      ['SmartHub'],
      ['Content'], // Home key
    ]
    for (const key of specialKeysObj) {
      try {
        this.logService.debug('Registering Special Key ' + key)
        webapis.appcommon.registerKey(key)
      } catch (err: any) {
        this.logService.error('Could not register special key + ' + key)
        this.logService.error(err.message)
      }
    }
  }

  initStorage(): void {
    const successCallback = (path: string) => {
      this.logService.info(`Successfully created directory ${path}`)
    }
    const errorCallback = (err: any) => {
      this.logService.error(`Could not create directory, error = ${err.message}`)
    }
    try {
      if (!tizen.filesystem.pathExists(this.MEDIA_PATH)) {
        tizen.filesystem.createDirectory(this.MEDIA_PATH, false, successCallback, errorCallback)
      }
    } catch (err: any) {
      this.logService.error(`Could not create directory (2), error = ${err.message}`)
    }
  }

  registerVolumeChangeListener(): void {
    const currentVolume = tizen.tvaudiocontrol.getVolume()
    this.logService.debug(`Current volume: ${currentVolume}`)
    const audioOutputMode = tizen.tvaudiocontrol.getOutputMode()
    this.logService.debug(`AudioOutputMode: ${audioOutputMode}`)
    const onVolumeChanged = (volume: string) => {
      this.logService.debug(`Volume changed to ${volume}`)
    }
    tizen.tvaudiocontrol.setVolumeChangeListener(onVolumeChanged)
  }

  reboot(): void {
    try {
      webapis.systemcontrol.rebootDevice()
    } catch (err: any) {
      this.logService.error(`Could not reboot device, error = ${err.message}`)
    }
  }

  // Language change will cause the app to reload causing the app to sometimes not boot correctly
  // Disabling it for now since it is only applying to Google Cast settings (and maybe other apps)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setLanguage(code: string): Promise<void> {
    /*
    const menuId = 'osd-language'
    const language = `${code.replace('-', '_')}.utf8` // ex: fr_FR.utf8, en_GB.utf8

    let currentLanguage: string | undefined = undefined
    try {
      currentLanguage = webapis.settings.getItemValue(menuId)
    } catch (err: any) {
      this.logService.error(`Could not get osd-language value, error = ${err?.message}`)
    }

    if (currentLanguage != language) {
      try {
        webapis.settings.setItemValue(menuId, language)
      } catch (err: any) {
        this.logService.error(`Could not set osd-language value, error = ${err?.message}`)
      }
    }
    */

    return Promise.resolve()
  }

  getPowerState(): Promise<PowerState | undefined> {
    return new Promise(resolve => {
      try {
        const tizenPowerState = webapis.remotepower.getPowerState()
        const powerState = tizenPowerState == 'NORMAL' ? PowerState.NORMAL : PowerState.STANDBY
        resolve(powerState)
      } catch (err: any) {
        this.logService.error(`Could not get power state, error = ${err.message}`)
        resolve(undefined)
      }
    })
  }

  setPowerState(state: PowerState): void {
    try {
      if (state == PowerState.STANDBY) {
        webapis.remotepower.powerOff()
      } else {
        webapis.remotepower.powerOn()
      }
    } catch (err: any) {
      this.logService.error(`Could not set power state, error = ${err.message}`)
    }
  }

  registerPowerStateListener(subject: BehaviorSubject<PowerState | undefined>): void {
    if (this.powerStateTimer) {
      clearInterval(this.powerStateTimer)
    }
    this.powerStateTimer = setInterval(async () => {
      const powerState = await this.getPowerState()
      if (this.powerState != powerState) {
        this.powerState = powerState
        subject.next(powerState)
      }
    }, this.powerStateTimerDelayInMs)
  }

  // Did not find an API that allows us to listen to the app installed state
  // Maybe https://docs.tizen.org/application/web/api/latest/device_api/tv/tizen/application.html#ApplicationManager::addAppStatusChangeListener
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  registerApplicationInstalledListener(subject: BehaviorSubject<string[]>): void {
    this.applicationInstalledSubject = subject
  }

  // Did not find an API that allows us to listen to the focus state
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  registerApplicationFocusListener(subject: BehaviorSubject<boolean>): void {
    // do nothing
  }

  getNetworkState(): Promise<NetworkState> {
    try {
      const gatewayStatus = webapis.network.isConnectedToGateway()
      const networkState = gatewayStatus ? NetworkState.CONNECTED : NetworkState.DISCONNECTED
      return Promise.resolve(networkState)
    } catch (err: any) {
      this.logService.error(`Could not retrieve isConnectedToGateway, error = ${err?.message}`)
      return Promise.resolve(NetworkState.UNKNOWN)
    }
  }

  getNetworkData(): Promise<NetworkData> {
    const connectionType = this.tizenInternal.getNetworkConnectionType()
    const networkData: NetworkData = {
      type: connectionType,
      macAddress: this.tizenInternal.getNetworkMac(),
      ipAddress: this.tizenInternal.getNetworkIp(),
      ipMode: this.tizenInternal.getNetworkIpMode(),
      subnetMask: this.tizenInternal.getNetworkSubnetMask(),
      gateway: this.tizenInternal.getNetworkGateway(),
      primaryDns: this.tizenInternal.getNetworkDns(),
      secondaryDns: this.tizenInternal.getNetworkSecondaryDns(),
      proxy: this.tizenInternal.getNetworkProxy(),
    }
    if (connectionType == NetworkConnectionType.WIFI) {
      const wifi: NetworkWifiData = {
        ssid: this.tizenInternal.getWiFiSsid(),
        signalStrengthLevel: this.tizenInternal.getWiFiSignalStrengthLevel(),
        securityMode: this.tizenInternal.getWiFiSecurityMode(),
        encryptionType: this.tizenInternal.getWiFiEncryptionType(),
        frequency: this.getWiFiFrequency(),
      }
      networkData.wifi = wifi
    }
    if (connectionType == NetworkConnectionType.ETHERNET) {
      if (this.tizenInternal.isSoftAPEnabled()) {
        const softAP: NetworkSoftAP = {
          enabled: true,
          ssid: this.tizenInternal.getSoftAPSSID(),
          securityKey: this.tizenInternal.getSoftAPSecurityKey(),
        }
        networkData.softAP = softAP
      }
    }
    return Promise.resolve(networkData)
  }

  getWiFiFrequency(): number {
    try {
      return webapis.network.getWiFiFrequency()
    } catch (err: any) {
      this.logService.error(`Could not retrieve network wifi frequency, error = ${err.message}`)
      return 0
    }
  }

  networkStateListenerId?: number
  networkStateRetryTimer?: ReturnType<typeof setTimeout>
  registerNetworkStateListener(subject: BehaviorSubject<NetworkState>): void {
    clearTimeout(this.networkStateRetryTimer)
    this.networkStateRetryTimer = undefined

    if (this.networkStateListenerId) {
      try {
        webapis.network.removeNetworkStateChangeListener(this.networkStateListenerId)
        this.networkStateListenerId = undefined
      } catch (err: any) {
        this.logService.error(`Error while unregistering network state listener, error = ${err.message}`)
      }
    }

    const listener = (state: TizenNetworkState) => {
      let networkState: NetworkState | undefined = undefined
      switch (state) {
        case TizenNetworkState.INTERNET_CONNECTED:
          networkState = NetworkState.CONNECTED
          break
        case TizenNetworkState.LAN_CABLE_ATTACHED:
          networkState = NetworkState.UNKNOWN
          break
        case TizenNetworkState.LAN_CABLE_DETACHED:
          networkState = NetworkState.UNKNOWN
          break
        case TizenNetworkState.LAN_CABLE_STATE_UNKNOWN:
          networkState = NetworkState.UNKNOWN
          break
        case TizenNetworkState.GATEWAY_CONNECTED:
          networkState = NetworkState.CONNECTED
          break
        case TizenNetworkState.GATEWAY_DISCONNECTED:
          networkState = NetworkState.DISCONNECTED
          break
        case TizenNetworkState.WIFI_MODULE_STATE_ATTACHED:
          networkState = NetworkState.UNKNOWN
          break
        case TizenNetworkState.WIFI_MODULE_STATE_DETACHED:
          networkState = NetworkState.UNKNOWN
          break
        case TizenNetworkState.WIFI_MODULE_STATE_UNKNOWN:
          networkState = NetworkState.UNKNOWN
          break
        case TizenNetworkState.INTERNET_DISCONNECTED:
          networkState = NetworkState.DISCONNECTED
          break
        default:
          networkState = NetworkState.UNKNOWN
          break
      }
      subject.next(networkState)
    }

    try {
      this.networkStateListenerId = webapis.network.addNetworkStateChangeListener(listener)
    } catch (err: any) {
      this.logService.error(`Error while registering network state listener, error = ${err.message}, retrying in 5s`)
      this.networkStateRetryTimer = setTimeout(() => this.registerNetworkStateListener(subject), 5000)
    }

    this.getNetworkState().then(networkState => subject.next(networkState))
  }

  getAppList(): Promise<TVApp[]> {
    return new Promise(resolve => {
      const onListInstalledApps = (appInfos: ApplicationInformation[]) => {
        resolve(
          appInfos.map(appInfo => ({
            id: appInfo.id,
            name: appInfo.name,
            iconPath: appInfo.iconPath,
            version: appInfo.version,
            internalApp: appInfo,
          }))
        )
      }
      tizen.application.getAppsInfo(onListInstalledApps)
    })
  }

  startApp(appId: string): void {
    const successCallback = () => {
      this.logService.info(`Successfully launched app ${appId}`)
    }
    const errorCallback = (err: any) => {
      this.logService.error(`Could not launch app ${appId} - error = ${err.message}`)
    }
    tizen.application.launch(appId, successCallback, errorCallback)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  startNetflixApp(appId: string, hotelId: string, launchMode: LocalAppLaunchMode): void {
    this.startApp(appId)
  }

  registerChannelListener(subject: BehaviorSubject<boolean>): void {
    this.channelChangedSubject = subject
  }

  startUDPChannel(channel: LocalChannel, pos: ChannelPosition): void {
    const source = channel.source as UDPChannel

    if (!source.ip) {
      this.logService.error('Invalid UDP channel configuration: missing IP')
      return
    }

    if (!source.port) {
      this.logService.error('Invalid UDP channel configuration: missing port')
      return
    }

    try {
      const protocol = source.rtp ? 'rtp' : 'udp'
      const url = `${protocol}://${source.ip}:${source.port}`
      const listener = {
        onbufferingstart: () => {
          this.logService.debug('Buffering start')
        },
        onbufferingprogress: (percent: any) => {
          this.logService.debug('Buffering progress: ' + percent + '%')
        },
        onbufferingcomplete: () => {
          this.logService.debug('Buffering complete')
        },
        onstreamcompleted: () => {
          this.logService.debug('Stream completed')
          webapis.avplay.stop()
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        oncurrentplaytime: (currentTime: any) => {
          //this.logService.debug('Current playtime: ' + currentTime)
        },
        onerror: (eventType: any) => {
          this.logService.error('(onerror) ' + eventType)
        },
        onerrormsg: (eventType: any, errorMsg: any) => {
          this.logService.debug(`(onerrormsg) ${eventType} ${errorMsg}`)
        },
        onevent: (eventType: any, eventData: any) => {
          this.logService.debug(`(onevent) ${eventType} data: ${JSON.stringify(eventData)}`)
        },
        onsubtitlechange: (duration: number, text: string, type: string, attributes: any) => {
          this.logService.debug(`(onsubtitlechange) ${duration} ${text} ${type} ${JSON.stringify(attributes)}`)
        },
        ondrmevent: (drmEvent: any, drmData: any) => {
          this.logService.debug(`(ondrmevent) ${drmEvent} data: ${JSON.stringify(drmData)}`)
        },
      }

      // Test if the URL is compatible before opening
      if (!url.startsWith('udp://') && !url.startsWith('rtp://')) {
        this.logService.error('The provided URL is not a valid UDP/RTP format: ' + url)
        return
      }

      this.logService.debug(`Loading UDP channel ${channel.name} on ${url}`)

      webapis.avplay.open(url)
      webapis.avplay.setDisplayRect(pos.left, pos.top, pos.width, pos.height)
      webapis.avplay.setListener(listener)
      webapis.avplay.prepareAsync(
        () => {
          this.logService.debug(`UDP channel ${channel.name} loaded`)
          this.setSubtitleState(false)
          webapis.avplay.play()
          this.channelChangedSubject?.next(true)
        },
        (err: any) => {
          this.logService.error(`Failed to prepare AVPlay for URL: ${url}. Error: ${err?.message}`)
          this.channelChangedSubject?.next(false)
        }
      )
    } catch (err: any) {
      this.logService.error(`Could not launch UDP channel. General error: ${err?.message}`)
      this.channelChangedSubject?.next(false)
    }
  }

  startWebChannel(channel: LocalChannel, pos: ChannelPosition): void {
    const source = channel.source as WebChannel

    if (!source.type) {
      this.logService.error(`Invalid WEB channel configuration: type = ${source.type}`)
      return
    }

    if (!source.url) {
      this.logService.error(`Invalid WEB channel configuration: url = ${source.url}`)
      return
    }

    const url = source.url
    const listener = {
      onbufferingstart: () => {
        this.logService.debug('Buffering start')
      },
      onbufferingprogress: (percent: any) => {
        this.logService.debug('Buffering progress: ' + percent + '%')
      },
      onbufferingcomplete: () => {
        this.logService.debug('Buffering complete')
      },
      onstreamcompleted: () => {
        this.logService.debug('Stream completed')
        webapis.avplay.stop()
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      oncurrentplaytime: (currentTime: any) => {
        //this.logService.debug('Current playtime: ' + currentTime)
      },
      onerror: (eventType: any) => {
        this.logService.error('(onerror) ' + eventType)
      },
      onerrormsg: (eventType: any, errorMsg: any) => {
        this.logService.debug(`(onerrormsg) ${eventType} ${errorMsg}`)
      },
      onevent: (eventType: any, eventData: any) => {
        this.logService.debug(`(onevent) ${eventType} data: ${JSON.stringify(eventData)}`)
      },
      onsubtitlechange: (duration: number, text: string, type: string, attributes: any) => {
        this.logService.debug(`(onsubtitlechange) ${duration} ${text} ${type} ${JSON.stringify(attributes)}`)
      },
      ondrmevent: (drmEvent: any, drmData: any) => {
        this.logService.debug(`(ondrmevent) ${drmEvent} data: ${JSON.stringify(drmData)}`)
      },
    }

    this.logService.debug(`Loading WEB channel ${channel.name} on ${url}`)

    try {
      webapis.avplay.open(url)
      webapis.avplay.setDisplayRect(pos.left, pos.top, pos.width, pos.height)
      webapis.avplay.setListener(listener)
      webapis.avplay.prepareAsync(
        () => {
          this.logService.debug(`WEB channel ${channel.name} loaded`)
          this.setSubtitleState(false)
          webapis.avplay.play()
          this.channelChangedSubject?.next(true)
        },
        (err: any) => {
          this.logService.error(`Failed to prepare AVPlay for URL: ${url}. Error: ${err.message}`)
          this.channelChangedSubject?.next(false)
        }
      )
    } catch (err: any) {
      this.logService.error(`Could not launch WEB channel. General error: ${err?.message}`)
      this.channelChangedSubject?.next(false)
    }
  }

  async startBroadcastChannel(channel: LocalChannel, pos: ChannelPosition): Promise<void> {
    const source = channel.source as DVBTChannel
    this.logService.debug(`Starting broadcast channel ${channel.name}`)
    try {
      const videoSource = await this.tizenInternal.getVideoSource('TV')
      await this.tizenInternal.setTVWindowVideoSource(videoSource)
      const rectangle = [pos.left, pos.top, pos.width, pos.height]
      await this.tizenInternal.showTVWindow(rectangle)
      await this.tuneDirect(source)
      this.channelChangedSubject?.next(true)
    } catch (err: any) {
      this.logService.error(`Could not start broadcast channel`)
      this.channelChangedSubject?.next(false)
    }
  }

  tuneDirect(source: DVBTChannel): Promise<void> {
    return new Promise((resolve, reject) => {
      const tuneOption = {
        broadcastStandard: 'DVB',
        channelType: 'DTV',
        frequency: source.freqHz / 1000,
        modulationType: 'QAM64',
        bandwidth: `${source.bandwidthMHz}MHz`,
        programNumber: source.serviceId,
      }
      const successCallback = (data: { result: string; data: string }) => {
        this.logService.debug(`[tuneDirect] success: ${data?.result}`)
        resolve()
      }
      const errorCallback = (err: any) => {
        this.logService.error(`[tuneDirect] [${err?.code}] ${err?.name}: ${err?.message}`)
        reject()
      }
      this.logService.debug(`[tuneDirect] ${JSON.stringify(tuneOption)}`)
      webapis.broadcast.tuneDirect(tuneOption, successCallback, errorCallback)
    })
  }

  stopUDPChannel(): void {
    try {
      webapis.avplay.stop()
    } catch (err: any) {
      this.logService.error(`Could not stop UDP channel, error = ${err.message}`)
    }
    try {
      webapis.avplay.close()
    } catch (err: any) {
      this.logService.error(`Could not close UDP channel, error = ${err.message}`)
    }
  }

  stopWebChannel(): void {
    try {
      webapis.avplay.stop()
    } catch (err: any) {
      this.logService.error(`Could not stop WEB channel, error = ${err.message}`)
    }
    try {
      webapis.avplay.close()
    } catch (err: any) {
      this.logService.error(`Could not close WEB channel, error = ${err.message}`)
    }
  }

  stopBroadcastChannel(): void {
    this.tizenInternal.hideTVWindow()
  }

  getCurrentChannel(): Promise<any> {
    return new Promise(resolve => {
      let count = 0
      const pollCurrentStreamInfo = () => {
        count++

        try {
          const state = webapis.avplay.getState()
          if (state == 'READY' || state == 'PLAYING' || state == 'PAUSED') {
            const currentStreamInfo = webapis.avplay.getCurrentStreamInfo()
            if (currentStreamInfo.length > 0) {
              return resolve(true)
            }
          }
        } catch (err: any) {
          // do nothing
        }

        if (count > 5) {
          return resolve(false)
        }
        setTimeout(pollCurrentStreamInfo, 500)
      }
      pollCurrentStreamInfo()
    })
  }

  getUDPCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    return this.getAVPlayCurrentProgramData()
  }

  getWebCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    return this.getAVPlayCurrentProgramData()
  }

  getAVPlayCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    const channelData: CurrentChannelData = {
      audioTracks: [],
      subtitleTracks: [],
      isSubtitleEnabled: false,
      currentAudioTrack: undefined,
      currentSubtitleTrack: undefined,
    }

    const mapToAudioTrack = (trackInfo: any, trackExtraInfo: any): ChannelTrackData => {
      const flags: string[] = []
      if (trackExtraInfo.audio_type && trackExtraInfo.audio_type == 3) {
        flags.push(ChannelTrackDataFlags.VISUAL_IMPAIRED)
      }
      const lang = trackExtraInfo?.track_lang ?? trackExtraInfo?.language ?? ''
      return {
        index: trackInfo.index,
        lang: this.tizenInternal.getLang(lang),
        data: trackExtraInfo,
        flags: flags,
      }
    }

    const mapToSubtitleTrack = (trackInfo: any, trackExtraInfo: any): ChannelTrackData => {
      const flags: string[] = []
      if (trackExtraInfo.subtitle_mode && trackExtraInfo.subtitle_mode == 1) {
        flags.push(ChannelTrackDataFlags.HEARING_IMPAIRED)
      }
      const lang = trackExtraInfo?.track_lang ?? trackExtraInfo?.language ?? ''
      return {
        index: trackInfo.index,
        lang: this.tizenInternal.getLang(lang),
        data: trackExtraInfo,
        flags: flags,
      }
    }

    const isSubtitleEnabled = this.tizenInternal.getSubtitleState()
    this.isSubtitleEnabled = isSubtitleEnabled
    channelData.isSubtitleEnabled = isSubtitleEnabled

    const state = webapis.avplay.getState()
    if (state == 'READY' || state == 'PLAYING' || state == 'PAUSED') {
      const currentStreamInfo = webapis.avplay.getCurrentStreamInfo()
      const totalTrackInfo = webapis.avplay.getTotalTrackInfo()

      if (totalTrackInfo.length == 0) {
        this.logService.debug(`Could not retrieve any tracks`)
        return Promise.resolve(undefined)
      }

      for (let i = 0; i < currentStreamInfo.length; i++) {
        const trackInfo = currentStreamInfo[i]
        const trackExtraInfo = JSON.parse(trackInfo.extra_info)
        switch (trackInfo.type) {
          case 'AUDIO': {
            channelData.currentAudioTrack = mapToAudioTrack(trackInfo, trackExtraInfo)
            break
          }
          case 'TEXT': {
            channelData.currentSubtitleTrack = mapToSubtitleTrack(trackInfo, trackExtraInfo)
            break
          }
          case 'VIDEO':
            break
          default:
            break
        }
      }

      for (let i = 0; i < totalTrackInfo.length; i++) {
        const trackInfo = totalTrackInfo[i]
        const trackExtraInfo = JSON.parse(trackInfo.extra_info)
        switch (trackInfo.type) {
          case 'AUDIO': {
            channelData.audioTracks.push(mapToAudioTrack(trackInfo, trackExtraInfo))
            break
          }
          case 'TEXT': {
            channelData.subtitleTracks.push(mapToSubtitleTrack(trackInfo, trackExtraInfo))
            break
          }
          case 'VIDEO':
            break
          default:
            break
        }
      }

      return Promise.resolve(channelData)
    } else {
      this.logService.debug(`Cannot retrieve tracks because state is invalid (state = ${state})`)
      return Promise.resolve(undefined)
    }
  }

  getBroadcastCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    const channelData: CurrentChannelData = {
      audioTracks: [],
      subtitleTracks: [],
      isSubtitleEnabled: false,
      currentAudioTrack: undefined,
      currentSubtitleTrack: undefined,
    }

    const mapToAudioTrack = (audioInfo: TizenAudioInfo): ChannelTrackData => {
      const flags: string[] = []
      switch (audioInfo.type) {
        case TizenAudioType.HEARING_IMPAIRED:
          flags.push(ChannelTrackDataFlags.HEARING_IMPAIRED)
          break
        case TizenAudioType.VISUAL_IMPAIRED:
          flags.push(ChannelTrackDataFlags.VISUAL_IMPAIRED)
          break
      }
      return {
        index: audioInfo.index,
        lang: this.tizenInternal.getLang(audioInfo.language),
        data: null,
        flags: flags,
      }
    }

    const mapToSubtitleTrack = (subtitleInfo: TizenSubtitleInfo): ChannelTrackData => {
      const flags: string[] = []
      if (subtitleInfo.mode === TizenSubtitleMode.HEARING_IMPAIRED) {
        flags.push(ChannelTrackDataFlags.HEARING_IMPAIRED)
      }
      return {
        index: subtitleInfo.index,
        lang: this.tizenInternal.getLang(subtitleInfo.language),
        data: null,
        flags: flags,
      }
    }

    const isSubtitleEnabled = this.tizenInternal.getSubtitleState()
    this.isSubtitleEnabled = isSubtitleEnabled
    channelData.isSubtitleEnabled = isSubtitleEnabled

    const audios = this.getBroadcastTotalAudio()
    channelData.audioTracks = audios.map(mapToAudioTrack)

    const currentAudio = this.getBroadcastCurrentAudioInfo()
    if (currentAudio) {
      channelData.currentAudioTrack = mapToAudioTrack(currentAudio)
    }

    const subtitles = this.getBroadcastTotalSubtitle()
    channelData.subtitleTracks = subtitles.map(mapToSubtitleTrack)

    const currentSubtitle = this.getBroadcastCurrentSubtitleInfo()
    if (currentSubtitle) {
      channelData.currentSubtitleTrack = mapToSubtitleTrack(currentSubtitle)
    }

    return Promise.resolve(channelData)
  }

  // Enable broadcast data service. Broadcast data service provides support for subtitles/captions and other broadcast functionalities.
  // This method should be called at the beginning of the web application.
  initBroadcastDataService(): void {
    const successCallback = () => {
      this.logService.debug(`Successfully enabled data service`)
    }
    const errorCallback = (err: any) => {
      this.logService.error(`Could enable data service, error = ${err.message}`)
    }
    try {
      webapis.broadcast.enableDataService(successCallback, errorCallback)
    } catch (err: any) {
      this.logService.error(`Could enable data service, error = ${err.message}`)
    }
  }

  getBroadcastCurrentAudioInfo(): TizenAudioInfo | undefined {
    try {
      return webapis.broadcast.getCurrentAudioInfo()
    } catch (err: any) {
      this.logService.error(`Could not retrieve current audio info, error = ${err.message}`)
      return undefined
    }
  }

  getBroadcastTotalAudio(): TizenAudioInfo[] {
    try {
      return webapis.broadcast.getTotalAudioInfo()
    } catch (err: any) {
      this.logService.error(`Could not retrieve total audio info, error = ${err.message}`)
      return []
    }
  }

  getBroadcastCurrentSubtitleInfo(): TizenSubtitleInfo | undefined {
    try {
      return webapis.broadcast.getCurrentSubtitle()
    } catch (err: any) {
      this.logService.error(`Could not retrieve current subtitle info, error = ${err.message}`)
      return undefined
    }
  }

  getBroadcastTotalSubtitle(): TizenSubtitleInfo[] {
    try {
      return webapis.broadcast.getTotalSubtitleInfo()
    } catch (err: any) {
      this.logService.error(`Could not retrieve total subtitle info, error = ${err.message}`)
      return []
    }
  }

  setUDPAudioTrack(track: ChannelTrackData): void {
    this.setAVPlayAudioTrack(track)
  }

  setWebAudioTrack(track: ChannelTrackData): void {
    this.setAVPlayAudioTrack(track)
  }

  setAVPlayAudioTrack(track: ChannelTrackData): void {
    try {
      webapis.avplay.setSelectTrack('AUDIO', track.index)
    } catch (err: any) {
      this.logService.error(`Could not set audio track ${track.index}, error = ${err.message}`)
    }
  }

  setBroadcastAudioTrack(track: ChannelTrackData): void {
    const successCallback = () => {
      this.logService.debug(`[setCurrentAudio] Successfully set audio track`)
    }
    const errorCallback = (err: any) => {
      this.logService.error(`[setCurrentAudio] Could not set audio track ${track.index}, error = ${err?.message}`)
    }
    try {
      webapis.broadcast.setCurrentAudio(track.index, successCallback, errorCallback)
    } catch (err: any) {
      this.logService.error(`[setCurrentAudio] Could not set audio track ${track.index}, error = ${err?.message}`)
    }
  }

  setUDPSubtitleTrack(track: ChannelTrackData): void {
    this.setAVPlaySubtitleTrack(track)
  }

  setWebSubtitleTrack(track: ChannelTrackData): void {
    this.setAVPlaySubtitleTrack(track)
  }

  setAVPlaySubtitleTrack(track: ChannelTrackData): void {
    try {
      webapis.avplay.setSelectTrack('TEXT', track.index)
    } catch (err: any) {
      this.logService.error(`Could not set subtitle track ${track.index}, error = ${err.message}`)
    }
  }

  setBroadcastSubtitleTrack(track: ChannelTrackData): void {
    const successCallback = () => {
      this.logService.debug(`[setCurrentSubtitleIndex] Successfully set subtitle track`)
    }
    const errorCallback = (err: any) => {
      this.logService.error(
        `[setCurrentSubtitleIndex] Could not set subtitle track ${track.index}, error = ${err.message}`
      )
    }
    try {
      webapis.broadcast.setCurrentSubtitleIndex(track.index, successCallback, errorCallback)
    } catch (err: any) {
      this.logService.error(
        `[setCurrentSubtitleIndex] Could not set subtitle track ${track.index}, error = ${err.message}`
      )
    }
  }

  setSubtitleState(enable: boolean): Promise<boolean> {
    const value = enable ? webapis.tvinfo.TvInfoMenuValue.ON : webapis.tvinfo.TvInfoMenuValue.OFF
    return new Promise((resolve, reject) => {
      const successCallback = () => {
        this.isSubtitleEnabled = enable
        resolve(enable)
      }
      const errorCallback = (err: any) => {
        this.logService.error(`Could not set subtitle state, error = ${err.message}`)
        reject()
      }
      try {
        const key = webapis.tvinfo.TvInfoMenuKey.SUBTITLE_ONOFF_KEY
        webapis.tvinfo.setMenuValue(key, value, successCallback, errorCallback)
      } catch (err: any) {
        this.logService.error(`Could not set subtitle state, error = ${err.message}`)
        reject()
      }
    })
  }

  getLocalMedias(): Promise<LocalMediaFile[]> {
    return new Promise((resolve, reject) => {
      const successCallback = (files: any) => {
        resolve(
          files.map((filename: string) => {
            return {
              id: filename.substring(0, filename.lastIndexOf('.')) || filename,
              filename: filename,
              url: tizen.filesystem.toURI(`${this.MEDIA_PATH}/${filename}`),
              sizeInBytes: undefined,
              sha1: undefined,
              md5: undefined,
            }
          })
        )
      }
      const errorCallback = (err: any) => {
        this.logService.error(`Could not list directory ${this.MEDIA_PATH}, error = ${err.message}`)
        reject()
      }
      try {
        tizen.filesystem.listDirectory(this.MEDIA_PATH, successCallback, errorCallback)
      } catch (err: any) {
        this.logService.error(`Could not list directory ${this.MEDIA_PATH} (2), error = ${err.message}`)
        reject()
      }
    })
  }

  downloadMedia(media: Media): Promise<LocalMediaFile> {
    const filename = `${media.id}.${media.fileExtension}`
    const path = `${this.MEDIA_PATH}/${filename}`

    if (tizen.filesystem.pathExists(path)) {
      return Promise.resolve({
        id: media.id,
        filename: filename,
        url: tizen.filesystem.toURI(path),
        sizeInBytes: undefined,
        sha1: undefined,
        md5: undefined,
      })
    } else {
      const src = `${media.url}?ts=${new Date().getTime()}`
      const downloadRequest = new tizen.DownloadRequest(src, this.MEDIA_PATH, filename)
      return new Promise((resolve, reject) => {
        const listener = {
          onprogress: (id: string, receivedSize: number, totalSize: number) => {
            this.logService.debug(`Download ${id} progress ${receivedSize}/${totalSize}`)
          },
          onpaused: (id: string) => {
            this.logService.info(`Download ${id} has been paused`)
          },
          oncanceled: (id: string) => {
            this.logService.error(`Download ${id} has been canceled`)
          },
          oncompleted: (id: string, fullPath: string) => {
            this.logService.info(`Download ${id} completed, full path: ${fullPath}`)
            return resolve({
              id: media.id,
              filename: filename,
              url: tizen.filesystem.toURI(path),
              sizeInBytes: undefined,
              sha1: undefined,
              md5: undefined,
            })
          },
          onfailed: (id: string, error: any) => {
            this.logService.error(`Download ${id} failed, error: ${error}`)
            reject()
          },
        }
        const downloadId = tizen.download.start(downloadRequest, listener)
        this.logService.info(`Downloading ${src} (id: ${downloadId})`)
      })
    }
  }

  removeMedia(mediaFile: LocalMediaFile): Promise<void> {
    const path = `${this.MEDIA_PATH}/${mediaFile.filename}`
    return new Promise((resolve, reject) => {
      const successCallback = (path: string) => {
        this.logService.info(`Successfully removed file at ${path}`)
        resolve()
      }
      const errorCallback = (err: any) => {
        this.logService.error(`Could not remove file ${path}, error = ${err.message}`)
        reject()
      }
      try {
        tizen.filesystem.deleteFile(path, successCallback, errorCallback)
      } catch (err: any) {
        this.logService.error(`Could not remove file ${path} (2), error = ${err.message}`)
        reject()
      }
    })
  }

  setNativeVolumeDisplay(enabled: boolean): void {
    const keys = ['VolumeUp', 'VolumeDown', 'VolumeMute']
    if (enabled) {
      keys.forEach(key => {
        try {
          this.logService.debug(`Unregistering Key ${key}`)
          tizen.tvinputdevice.unregisterKey(key)
        } catch (err: any) {
          this.logService.error(`Could not unregister key ${key}, error = ${err?.message}`)
        }
      })
    } else {
      keys.forEach(key => {
        try {
          this.logService.debug(`Registering Key ${key}`)
          tizen.tvinputdevice.registerKey(key)
        } catch (err: any) {
          this.logService.error(`Could not register key ${key}, error = ${err?.message}`)
        }
      })
    }
  }

  getVolume(): Promise<number> {
    return Promise.resolve(tizen.tvaudiocontrol.getVolume())
  }

  setVolume(volume: number): Promise<number> {
    try {
      tizen.tvaudiocontrol.setVolume(volume)
      return Promise.resolve(tizen.tvaudiocontrol.getVolume())
    } catch (err: any) {
      this.logService.error(`Could not set volume, error = ${err.message}`)
      return Promise.resolve(0)
    }
  }

  setVolumeDown(): Promise<number> {
    try {
      tizen.tvaudiocontrol.setVolumeDown()
      return Promise.resolve(tizen.tvaudiocontrol.getVolume())
    } catch (err: any) {
      this.logService.error(`Could not set volume up, error = ${err.message}`)
      return Promise.resolve(0)
    }
  }

  setVolumeUp(): Promise<number> {
    try {
      tizen.tvaudiocontrol.setVolumeUp()
      return Promise.resolve(tizen.tvaudiocontrol.getVolume())
    } catch (err: any) {
      this.logService.error(`Could not set volume up, error = ${err.message}`)
      return Promise.resolve(0)
    }
  }

  getVolumeMute(): Promise<boolean> {
    return Promise.resolve(tizen.tvaudiocontrol.isMute())
  }

  setVolumeMute(muted: boolean): Promise<boolean> {
    try {
      tizen.tvaudiocontrol.setMute(muted)
      return Promise.resolve(tizen.tvaudiocontrol.isMute())
    } catch (err: any) {
      this.logService.error(`Could not change mute status, error = ${err.message}`)
      return Promise.resolve(false)
    }
  }

  getExternalInputs(): Promise<ExternalInputData[]> {
    return this.tizenInternal.getExternalInputs()
  }

  // Disabled since it is causing TV crash when launching the HDMI with the listener registered
  // Disabling the listener before launching the HDMI source did not help with the crash
  // So for Samsung Tizen, we only check the HDMI ports status during startup
  registerExternalInputListener(subject: BehaviorSubject<ExternalInputData[]>): void {
    this.tizenInternal.registerExternalInputListener(subject)
  }

  async showHDMI(externalInput: ExternalInputData): Promise<void> {
    try {
      const videoSource = await this.tizenInternal.getVideoSource('HDMI', externalInput.number)
      await this.tizenInternal.setTVWindowVideoSource(videoSource)
      await this.tizenInternal.showTVWindow(['0', '0', '100%', '100%'])
    } catch (err: any) {
      this.logService.error(`Could not show HDMI source, error = ${err?.message}`)
    }
  }

  hideHDMI(): Promise<void> {
    this.tizenInternal.hideTVWindow()
    return Promise.resolve()
  }

  setupCustomAppInfo(deviceId: string | undefined): void {
    if (environment.localEnv) {
      return
    }

    let customAppInfo: any
    let platformVersion: string

    try {
      customAppInfo = webapis.systemcontrol.getCustomAppInfo()
    } catch (err: any) {
      this.logService.error(`Could not get CustomAppInfo, error = ${err?.message}`)
      return
    }

    try {
      platformVersion = tizen.systeminfo.getCapability('http://tizen.org/feature/platform.version')
    } catch (err: any) {
      this.logService.error(`Could not retrieve Tizen platform version, error = ${err?.message}`)
      return
    }

    const downloadServerAddress = deviceId
      ? `${environment.appUrl}/tizen/${platformVersion}/${deviceId}`
      : `${environment.appUrl}/tizen/${platformVersion}`

    if (customAppInfo.downloadServerAddress != downloadServerAddress) {
      try {
        webapis.systemcontrol.setCustomAppInfo({
          downloadServerAddress: downloadServerAddress,
          connectionTimeout: '30SEC',
        })
      } catch (err: any) {
        this.logService.error(`Could not set CustomAppInfo, error = ${err?.message}`)
      }
    }
  }

  wasInitSIToken(token: string): boolean {
    try {
      this.logService.debug(`[Tizen] Initializing SIToken ...`)
      webapis.was.initializeSIToken(token)
      return true
    } catch (err: any) {
      if (err.message === 'ERROR_TOKEN_ALREADY_INITIALIZED') {
        return true
      } else {
        this.logService.error(
          `An error occurred during webapis.was.initializeSIToken(${token}), error = ${err?.message}`
        )
        return false
      }
    }
  }

  wasGetCapability(appId: string): boolean {
    try {
      const capability = webapis.was.getCapability(appId)
      if (capability === 'CAPABILITY_ENABLE') {
        return true
      } else {
        this.logService.info(`Unhandled capability ${capability} for app ${appId}`)
        return false
      }
    } catch (err: any) {
      this.logService.error(`An error occurred during webapis.was.getCapability(${appId}), error = ${err?.message}`)
      return false
    }
  }

  wasInstallApp(appId: string): void {
    const listener = {
      oninstallstart: (id: string) => {
        this.logService.info(`Starting installation of app ${id}`)
      },
      ondownload: (id: string, progress: number) => {
        this.logService.info(`Download in progress for app ${id}, progress: ${progress}`)
      },
      oninstallation: (id: string, progress: number) => {
        this.logService.info(`Installation in progress for app ${id}, progress: ${progress}`)
      },
      oninstallsuccess: (id: string) => {
        this.logService.info(`Installation success for app ${id}`)
        this.isNetflixInstalled = this.checkNetflixInstalled()
        this.applicationInstalledSubject?.next([id])
      },
      oninstallerror: (id: string, err: any) => {
        this.logService.error(`Installation failed for app ${id}, error = ${err?.message}`)
      },
      onerror: (err: any) => {
        this.logService.error(`An error occurred during installation of app, error = ${err?.message}`)
      },
    }
    try {
      webapis.was.installApp(appId, listener)
    } catch (err: any) {
      this.logService.error(`An error occurred during webapis.was.installApp(${appId}), error = ${err?.message}`)
    }
  }

  // TODO: Not sure what is the factor to look up, maybe firmware version ?
  hasIntegratedGoogleCast(): boolean {
    return true
  }

  initGoogleCastApp(): void {
    if (this.wasInitSIToken(this.GOOGLECAST_TOKEN)) {
      this.gcInitializedSubject?.next(true)
    } else {
      this.gcInitializedSubject?.next(false)
    }
  }

  registerGoogleCastInitializedListener(subject: BehaviorSubject<boolean | undefined>): void {
    this.gcInitializedSubject = subject
  }

  disableGoogleCast(): Promise<void> {
    return Promise.resolve()
  }

  initGoogleCastProxy(): Promise<void> {
    return Promise.resolve()
  }

  initGoogleCastSoftAP(): Promise<void> {
    return Promise.resolve()
  }

  acceptGoogleCastTermsOfService(): Promise<void> {
    try {
      webapis.googlecast.initialize()
      webapis.googlecast.acceptTermsOfService()
      webapis.googlecast.finalize()
      return Promise.resolve()
    } catch (err: any) {
      this.logService.error(`An error occurred during Google Cast TOS initialization, error = ${err?.message}`)
      return Promise.reject()
    }
  }

  enableGoogleCastUsageReporting(): Promise<void> {
    try {
      webapis.googlecast.initialize()
      const usageReportingStatus = webapis.googlecast.getUsageReportingStatus()
      if (usageReportingStatus === 'DEFAULT') {
        webapis.googlecast.enableUsageReporting()
      }
      webapis.googlecast.finalize()
      return Promise.resolve()
    } catch (err: any) {
      this.logService.error(`An error occurred during Google Cast enable usage reporting, error = ${err?.message}`)
      return Promise.reject()
    }
  }

  clearGoogleCastData(): Promise<void> {
    try {
      webapis.googlecast.initialize()
      webapis.googlecast.clearData()
      webapis.googlecast.finalize()
      return Promise.resolve()
    } catch (err: any) {
      this.logService.error(`An error occurred during googlecast clear data, error = ${err?.message}`)
      return Promise.reject()
    }
  }

  openGoogleCastLocalPage(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    roomNumber: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    supportMessage: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    gcStateSubject: BehaviorSubject<GoogleCastState | undefined>
  ): Promise<void> {
    this.routerService.goToPage(RouterPage.CAST)
    return Promise.resolve()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  openGoogleCastSoftAPPage(roomNumber: string, pairingStatus: boolean, supportMessage: string): Promise<void> {
    this.routerService.goToPage(RouterPage.CAST)
    return Promise.resolve()
  }

  pollGoogleCastSoftAP(): Promise<any> {
    const softAPSSID = this.tizenInternal.getSoftAPSSID()
    const softAPSecurityKey = this.tizenInternal.getSoftAPSecurityKey()
    return Promise.resolve({
      ssid: softAPSSID,
      password: softAPSecurityKey,
    })
  }

  googleCastStatusListener(newStatus: string) {
    this.logService.debug(`[Tizen] Google Cast status changed to: ${newStatus}`)
    try {
      if (newStatus !== 'INIT') {
        webapis.googlecast.launchSettings('ShowInstructions:false,HideButtons:false')
        webapis.googlecast.unregisterStatusChangeListener()
        webapis.googlecast.finalize()
      }
    } catch (err: any) {
      this.logService.error(`[Tizen] ${err?.code}`)
    }
  }

  launchGoogleCastSettings(): Promise<void> {
    try {
      webapis.googlecast.initialize()
      const status = webapis.googlecast.getStatus()
      if (status === 'INIT') {
        webapis.googlecast.registerStatusChangeListener(this.googleCastStatusListener)
      } else {
        webapis.googlecast.launchSettings('ShowInstructions:false,HideButtons:false')
        webapis.googlecast.finalize()
      }
      return Promise.resolve()
    } catch (err: any) {
      this.logService.error(`An error occurred during googlecast launchSettings, error = ${err?.message}`)
      return Promise.resolve()
    }
  }

  async handleNetflixApp(): Promise<void> {
    if (this.netflixEnabled) {
      if (!this.netflixToken || this.netflixToken.length === 0) {
        this.netflixToken = this.NETFLIX_TOKEN
      }
      if (!this.netflixAppId || this.netflixAppId.length === 0) {
        this.netflixAppId = this.NETFLIX_APP_ID
      }
      if (!this.netflixAppName || this.netflixAppName.length === 0) {
        this.netflixAppName = this.NETFLIX_APP_NAME
      }
      if (!this.checkNetflixInstalled()) {
        this.installNetflixApp()
      } else {
        this.logService.info(`Netflix is already installed`)
      }
    } else {
      this.logService.debug(`Netflix is disabled in Humecloud configuration`)
    }
  }

  async installNetflixApp(): Promise<void> {
    if (!this.netflixToken || !this.netflixAppId) {
      this.logService.error('Could not find Netflix token and appId')
      return
    }

    const install = async (token: string, appId: string, count: number) => {
      count++
      this.logService.info(`[Tizen] Starting installation of app ${appId} (${count})`)
      if (await this.wasInitSIToken(token)) {
        if (this.wasGetCapability(appId)) {
          this.wasInstallApp(appId)
        } else {
          if (count < 3) {
            setTimeout(install.bind(this, token, appId, count), 1000)
          } else {
            this.logService.error(`[Tizen] Could not install app after 3 tries, aborting`)
          }
        }
      } else {
        if (count < 3) {
          setTimeout(install.bind(this, token, appId, count), 1000)
        } else {
          this.logService.error(`[Tizen] Could not install app after 3 tries, aborting`)
        }
      }
    }

    install(this.netflixToken, this.netflixAppId, 0)
  }

  checkNetflixInstalled(): boolean {
    if (this.netflixAppName) {
      const appInfo = this.tizenInternal.getAppInfo(this.netflixAppName)
      return appInfo && (appInfo.name == 'Netflix' || appInfo.name == 'netflix')
    } else {
      return false
    }
  }

  adminOpenAppsSettings(): void {
    this.startApp('com.samsung.tv.store')
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  downloadAndUpgradeApp(deviceId: string): void {
    // do nothing
  }

  canDownloadAndUpgradeApp(): boolean {
    return false
  }

  // Using Shaka player for now since it's easier to maintain only one accross various platforms
  hasCustomVOD(): boolean {
    return false
  }

  getShakaPlatformConfig(): any {
    return undefined
  }

  vodStart(vod: VODVideo, pos: ChannelPosition): void {
    let drmSessionId: string | undefined
    const drmAppSession = String(Math.floor(Math.random() * Math.floor(10000)))

    const listener = {
      onbufferingstart: () => {
        this.logService.debug('Buffering start')
      },
      onbufferingprogress: (percent: any) => {
        this.logService.debug('Buffering progress: ' + percent + '%')
      },
      onbufferingcomplete: () => {
        this.logService.debug('Buffering complete')
      },
      onstreamcompleted: () => {
        this.logService.debug('Stream completed')
        vod.state?.status$.next(VODStatus.COMPLETED)
        webapis.avplay.stop()
      },
      oncurrentplaytime: (currentTime: any) => {
        vod.state?.currentTime$.next(currentTime)
      },
      onerror: (eventType: any) => {
        this.logService.error('(onerror) ' + eventType)
      },
      onerrormsg: (eventType: any, errorMsg: any) => {
        this.logService.debug(`(onerrormsg) ${eventType} ${errorMsg}`)
      },
      onevent: (eventType: any, eventData: any) => {
        this.logService.debug(`(onevent) ${eventType} data: ${JSON.stringify(eventData)}`)
      },
      onsubtitlechange: (duration: number, text: string, type: string, attributes: any) => {
        this.logService.debug(`(onsubtitlechange) ${duration} ${text} ${type} ${JSON.stringify(attributes)}`)
        vod.state?.subtitleChange$.next({ duration, text })
      },
      ondrmevent: (drmEvent: any, drmData: any) => {
        try {
          this.logService.debug(`(ondrmevent) ${drmEvent} data: ${JSON.stringify(drmData)}`)

          if (drmData.name === 'Challenge' && vod.drm && vod.drmConfig && vod.drmConfig.widevineLicenseUrl) {
            drmSessionId = drmData.session_id

            this.tizenInternal.widevineDrmChallenge(vod.drmConfig.widevineLicenseUrl, drmData.challenge).subscribe({
              next: (license: string) => {
                webapis.avplay.setDrm(
                  'WIDEVINE_CDM',
                  'widevine_license_data',
                  drmSessionId + 'PARAM_START_POSITION' + btoa(license) + 'PARAM_START_POSITION'
                )
              },
              error: () => {
                this.logService.debug(`Could not retrieve drm license from Widevine`)
              },
            })
          } else if (drmData.name === 'DrmError') {
            this.logService.error(`(ondrmevent) DRM Error: [${drmData.code}] ${drmData.message}`)
          }
        } catch (err: any) {
          this.logService.error(`(ondrmevent) Could not handle data`)
        }
      },
    }

    this.logService.debug(`Loading VOD ${vod.id}`)

    vod.state?.status$.next(VODStatus.LOADING)
    vod.state?.duration$.next(undefined)
    vod.state?.currentTime$.next(undefined)

    webapis.avplay.open(vod.videoUrl)
    webapis.avplay.setDisplayRect(pos.left, pos.top, pos.width, pos.height)
    webapis.avplay.setListener(listener)

    if (vod.drm) {
      webapis.avplay.setDrm(
        'WIDEVINE_CDM',
        'SetProperties',
        JSON.stringify({
          AppSession: drmAppSession,
          DataType: 'MPEG-DASH',
        })
      )
    }

    webapis.avplay.prepareAsync(
      () => {
        this.logService.debug(`VOD ${vod.id} loaded`)
        this.setSubtitleState(false)

        vod.state?.status$.next(VODStatus.READY)
        vod.state?.duration$.next(webapis.avplay.getDuration())
        vod.state?.currentTime$.next(webapis.avplay.getCurrentTime())
      },
      (err: any) => {
        this.logService.error(`Failed to prepare AVPlay for URL: ${vod.videoUrl}, error = ${err?.message}`)
      }
    )
  }

  vodPlay(vod: VODVideo): void {
    try {
      const state = webapis.avplay.getState()
      if (state == 'IDLE') {
        vod.state?.status$.next(VODStatus.LOADING)
        vod.state?.duration$.next(undefined)
        vod.state?.currentTime$.next(undefined)

        webapis.avplay.prepareAsync(
          () => {
            this.logService.debug(`VOD ${vod.id} loaded`)

            vod.state?.status$.next(VODStatus.READY)
            vod.state?.duration$.next(webapis.avplay.getDuration())
            vod.state?.currentTime$.next(webapis.avplay.getCurrentTime())

            webapis.avplay.play()
            vod.state?.status$.next(VODStatus.PLAYING)
          },
          (err: any) => {
            this.logService.error(`Failed to prepare AVPlay for URL: ${vod.videoUrl}, error = ${err?.message}`)
          }
        )
      } else {
        webapis.avplay.play()
        vod.state?.status$.next(VODStatus.PLAYING)
      }
    } catch (err: any) {
      this.logService.error(`(webapis.avplay.play) error = ${err?.message}`)
    }
  }

  vodPause(vod: VODVideo): void {
    try {
      webapis.avplay.pause()
      vod.state?.status$.next(VODStatus.PAUSED)
    } catch (err: any) {
      this.logService.error(`(webapis.avplay.pause) error = ${err?.message}`)
    }
  }

  vodStop(): void {
    try {
      webapis.avplay.stop()
    } catch (err: any) {
      this.logService.error(`(webapis.avplay.stop) error = ${err?.message}`)
    }
    try {
      webapis.avplay.close()
    } catch (err: any) {
      this.logService.error(`(webapis.avplay.close) error = ${err.message}`)
    }
  }

  vodGetCurrentTime(): number {
    try {
      return webapis.avplay.getCurrentTime()
    } catch (err: any) {
      this.logService.error(`(webapis.avplay.getCurrentTime) error = ${err?.message}`)
      return 0
    }
  }

  vodSpeed(vod: VODVideo, speed: number): void {
    try {
      const state = webapis.avplay.getState()
      if (state == 'READY' || state == 'PLAYING' || state == 'PAUSED') {
        if (speed != 1) {
          this.vodPlay(vod)
        }
        webapis.avplay.setSpeed(speed)
      } else {
        this.logService.debug(`(webapis.avplay.setSpeed) invalid state = ${state}`)
      }
    } catch (err: any) {
      this.logService.error(`(webapis.avplay.setSpeed) error = ${err?.message}`)
    }
  }

  vodSeekTo(vod: VODVideo, milliseconds: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isSeeking) {
        this.isSeeking = true
        try {
          const state = webapis.avplay.getState()
          if (state == 'IDLE' || state == 'READY' || state == 'PLAYING' || state == 'PAUSED') {
            webapis.avplay.seekTo(
              milliseconds,
              () => {
                this.logService.debug('(webapis.avplay.seekTo) success')
                vod.state?.currentTime$.next(webapis.avplay.getCurrentTime())
                this.isSeeking = false
                resolve()
              },
              (err: any) => {
                this.logService.error('(webapis.avplay.seekTo) err = ' + err?.message)
                this.isSeeking = false
                reject()
              }
            )
          } else {
            this.logService.debug(`(webapis.avplay.seekTo) invalid state = ${state}`)
            this.isSeeking = false
            reject()
          }
        } catch (err: any) {
          this.logService.error(`(webapis.avplay.seekTo) error = ${err?.message}`)
          this.isSeeking = false
          reject()
        }
      } else {
        this.logService.debug(`(webapis.avplay.seekTo) Already seeking, aborting seekTo(${milliseconds})`)
        resolve()
      }
    })
  }

  vodJumpBackward(vod: VODVideo, milliseconds: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isSeeking) {
        this.isSeeking = true
        try {
          const state = webapis.avplay.getState()
          if (state == 'READY' || state == 'PLAYING' || state == 'PAUSED') {
            webapis.avplay.jumpBackward(
              milliseconds,
              () => {
                this.logService.debug('(webapis.avplay.jumpBackward) success')
                vod.state?.currentTime$.next(webapis.avplay.getCurrentTime())
                this.isSeeking = false
                resolve()
              },
              (err: any) => {
                this.logService.debug('(webapis.avplay.jumpBackward) err = ' + err?.message)
                this.isSeeking = false
                reject()
              }
            )
          } else {
            this.logService.debug(`(webapis.avplay.jumpBackward) invalid state = ${state}`)
            this.isSeeking = false
            reject()
          }
        } catch (err: any) {
          this.logService.error(`(webapis.avplay.jumpBackward) error = ${err?.message}`)
          this.isSeeking = false
          reject()
        }
      } else {
        this.logService.debug(`(webapis.avplay.jumpBackward) Already seeking, aborting seekTo(${milliseconds})`)
        resolve()
      }
    })
  }

  vodJumpForward(vod: VODVideo, milliseconds: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isSeeking) {
        this.isSeeking = true
        try {
          const state = webapis.avplay.getState()
          if (state == 'READY' || state == 'PLAYING' || state == 'PAUSED') {
            webapis.avplay.jumpForward(
              milliseconds,
              () => {
                this.logService.debug('(webapis.avplay.jumpForward) success')
                vod.state?.currentTime$.next(webapis.avplay.getCurrentTime())
                this.isSeeking = false
                resolve()
              },
              (err: any) => {
                this.logService.debug('(webapis.avplay.jumpForward) err = ' + err?.message)
                this.isSeeking = false
                reject()
              }
            )
          } else {
            this.logService.debug(`(webapis.avplay.jumpForward) invalid state = ${state}`)
            this.isSeeking = false
            reject()
          }
        } catch (err: any) {
          this.logService.error(`(webapis.avplay.jumpForward) error = ${err?.message}`)
          this.isSeeking = false
          reject()
        }
      } else {
        this.logService.debug(`(webapis.avplay.jumpForward) Already seeking, aborting seekTo(${milliseconds})`)
        resolve()
      }
    })
  }
  initVirtualKeyboard(): void {
    return
  }
}
